import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, FormControl } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';


import { UirouterMigrationService } from 'Utilities/uirouter.migration.service';
import { ChainService } from './chain.service';
import { MerchantService } from 'OpsCenter/merchants/merchant.service';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-chains-create',
  template: require('./chains.create.component.html'),
})

/** @desc Container component to handle creation of chains **/
export class ChainsCreateComponent implements OnInit {
  isLoading = false;
  merchantId: number;
  merchant = null;
  createChainForm: FormGroup;

  constructor(
    private notificationService: ToastrService,
    private chainService: ChainService,
    private fb: FormBuilder,
    private uirouterMigrationService: UirouterMigrationService,
    private merchantService: MerchantService,
    private translate: TranslateService,
    private cd: ChangeDetectorRef,
  ) {
    this.merchantId = this.uirouterMigrationService.getParams('app.chains-create').merchantId;
  }

  ngOnInit() {
    this.createForm();
    if (this.merchantId) {
      this.getMerchant();
    }
  }

  /**
   * @desc Function to create the form
   * @return {void}
   */
  createForm() {
    this.createChainForm = this.fb.group({
      chainName: new FormControl(),
    });

    // TODO remove when no hybrid app
    this.createChainForm.get('chainName').valueChanges.subscribe(() => this.cd.detectChanges());
  }

  /**
   * @desc Function to retrieve merchant information to be used for adding to the chain
   * @return {void}
   */
  getMerchant(): void {
    this.enableLoading();
    this.merchantService
      .getMerchantById(this.merchantId)
      .pipe(finalize(() => {
        this.disableLoading();
      }))
      .subscribe(res => {
        this.merchant = res;
        this.createChainForm.get('chainName').setValue(this.merchant.name);
        this.cd.detectChanges();
      });
  }

  /**
   * @desc Function to trigger adding a new chain
   * @return {void}
   */
  createChain(): void {
    if (!this.createChainForm.value.chainName) {
      return;
    }
    this.enableLoading();

    this.chainService
      .createChain(this.createChainForm.value.chainName)
      .pipe(
        finalize(() => {
          this.disableLoading();
          this.cd.detectChanges();
        }),
      )
      .subscribe(
        res => {
          const chainId = this.getDestinationIdFromLocationHeader(res.headers.get('location'));
          this.notificationService.success('Chain successfully created');
          if (this.merchant) {
            this.addMerchantToChain(parseInt(chainId, 10), this.merchant.id);
          } else {
            this.uirouterMigrationService.goTo('app.chains-edit', { chainId });
          }
        },
        res => {
          const error = Array.isArray(res.error.errors) ? res.error.errors.shift() : res.error,
            errorMessage = error.message ? error.message : 'Chain creation failed';
          this.translate.get(errorMessage).subscribe((res: string) => {
            this.notificationService.error(res);
          });
        },
      );
  }

  /**
   * @desc Function to add given merchant to the newly created chain
   * @param {number} chainId - The id of the newly created chain
   * @param {number} merchantId - The id of the merchant to be added to the chain
   * @return {void}
   */
  addMerchantToChain(chainId: number, merchantId: number): void {
    this.chainService.addMerchantToChain(chainId, merchantId)
      .pipe(finalize(() => this.cd.detectChanges())).subscribe(
      () => {
        this.uirouterMigrationService.goTo('app.chains-edit', { chainId });
      },
      res => {
        const error = Array.isArray(res.error.errors) ? res.error.errors.shift() : res.error,
          errorMessage = error.message ? error.message : 'Restaurant addition to chain failed';
        this.translate.get(errorMessage).subscribe((res: string) => {
          this.notificationService.error(res);
        });
      },
    );
  }

  /**
   * @desc Helper function to extract the chain id from the response header of the create request
   * @param {string} locationHeader - The header response that includes the chain id
   * @return {string}
   */
  getDestinationIdFromLocationHeader(locationHeader: string): string {
    return locationHeader.split('/').pop();
  }

  /**
   * @desc Helper function to enable loading
   * @return {void}
   */
  enableLoading(): void {
    this.isLoading = true;
  }

  /**
   * @desc Helper function to disable loading
   * @return {void}
   */
  disableLoading(): void {
    this.isLoading = false;
  }
}



// WEBPACK FOOTER //
// ./src/app/chains/chains.create.component.ts